import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { parseISO, differenceInMinutes, subMonths, subYears, isValid as isValidDate } from 'date-fns';
import './ProjectsKPI.css';
import { getProjects } from '../../actions/projectActions';

// Prefer the reducer-persisted key used by projectReducer ("projects") with fallback to our old key
const getProjectsFromStorage = () => {
  const raw = localStorage.getItem('projects') || localStorage.getItem('userProjects');
  try {
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Prefer a project's CREATION date; fallback to other fields if missing
function getProjectCreationDate(project) {
  // Strong signals for creation
  const candidates = [
    project?.creationDate,
    project?.createdAt,
    // weak fallbacks when creation not present
    project?.modificationDate?.date || project?.modificationDate,
    project?.updatedAt,
    project?.date,
  ];
  for (const c of candidates) {
    if (!c) continue;
    // If already a Date
    if (c instanceof Date && !isNaN(c.getTime())) return c;
    // If string
    if (typeof c === 'string') {
      // Try ISO first
      const d1 = parseISO(c);
      if (isValidDate(d1)) return d1;
      const d2 = new Date(c);
      if (!isNaN(d2.getTime())) return d2;
    }
    // If object (e.g., Firestore Timestamp-like or Mongo $date)
    if (typeof c === 'object') {
      if (c?.$date) {
        const d = new Date(c.$date);
        if (!isNaN(d.getTime())) return d;
      }
      if (c?.seconds) {
        const d = new Date(c.seconds * 1000);
        if (!isNaN(d.getTime())) return d;
      }
      if (c?.toDate && typeof c.toDate === 'function') {
        const d = c.toDate();
        if (d instanceof Date && !isNaN(d.getTime())) return d;
      }
    }
  }
  return null; // Unknown
}

function withinPeriod(date, selectedPeriod) {
  if (!date) return false;
  const now = new Date();
  switch (selectedPeriod) {
    case "Aujourd'hui":
      return differenceInMinutes(now, date) <= 1440;
    case '7 derniers jours':
      return differenceInMinutes(now, date) <= 10080;
    case '30 derniers jours':
      return differenceInMinutes(now, date) <= 43200;
    case '3 derniers mois': {
      const threshold = subMonths(now, 3);
      return date >= threshold;
    }
    case '1 dernière année': {
      const threshold = subYears(now, 1);
      return date >= threshold;
    }
    default:
      return true;
  }
}

const ProjectsKPI = ({ selectedPeriod = '7 derniers jours' }) => {
  const dispatch = useDispatch();
  const storeProjects = useSelector((state) => state?.project?.projects);
  const [count, setCount] = useState(0);
  const requestedRef = useRef(false);

  // Choose source: Redux if available, else storage
  const { projects, fromRedux } = useMemo(() => {
    if (Array.isArray(storeProjects) && storeProjects.length > 0) {
      // Keep storage synced for dashboards opened in other spaces
      try { localStorage.setItem('projects', JSON.stringify(storeProjects)); } catch {}
      return { projects: storeProjects, fromRedux: true };
    }
    return { projects: getProjectsFromStorage(), fromRedux: false };
  }, [storeProjects]);

  useEffect(() => {
    // If no projects in store AND storage is empty, try fetching once in background
    if ((!Array.isArray(storeProjects) || storeProjects?.length === 0) && projects.length === 0 && !requestedRef.current) {
      requestedRef.current = true;
      try { dispatch(getProjects()); } catch {}
    }

    if (!Array.isArray(projects)) {
      setCount(0);
      return;
    }

    // Determine creation dates and apply period filtering regardless of source
    const withDates = projects.map(p => ({ p, d: getProjectCreationDate(p) })).filter(x => x.p);
    const validDates = withDates.filter(x => x.d);

    // If absolutely no date info on any project, fall back:
    // - If Redux source, assume unknown dates and show 0 (cannot determine created-within-period)
    // - If storage source, show total to avoid misleading zero for older datasets
    if (validDates.length === 0) {
      setCount(fromRedux ? 0 : projects.length);
      return;
    }

    const filtered = validDates.filter(x => withinPeriod(x.d, selectedPeriod));
    setCount(filtered.length);
  }, [projects, selectedPeriod, fromRedux, storeProjects, dispatch]);

  return (
    <div className="kpi-card">
      <h2 className="kpi-value">{count}</h2>
      <p className="kpi-label">Projets par utilisateur</p>
    </div>
  );
};

export default ProjectsKPI;
